import { z } from 'zod';
import { baseEntityFields, idSchema, instanceIdSchema, isoDateTimeSchema, queryNumberSchema, timezoneSchema, } from './common.schema';
import { datapointNameSchema, sensorKeySchema } from './sensor.schema';
/**
 * Learned baselines (#18) — "what is normal for this entity". Computed in
 * BigQuery from the hourly rollups ("aggregate in SQL, not JS"), seasonal
 * in the TENANT'S local time (business hours are local), robust to the
 * odd bad hour. Design, storage, refresh cadence and cost bound:
 * docs/architecture/baselines.md.
 *
 * This is the ONE definition of the model: the BigQuery refresh job, the
 * read API, and the consumers (#26 change-window grading, #15 tuning
 * proposals) all take their parameters from here, so baseline math exists
 * once.
 */
export const BASELINE_MODEL = {
    /** Bumped whenever the computation changes; stored on every row. */
    version: 1,
    /** Trailing window of COMPLETE hours the baseline is learned from. */
    windowDays: 28,
    /** A series needs this many distinct hours of data to have any baseline. */
    minHours: 24,
    /** ...this many for an hour-of-day profile (24 buckets, ~3 samples each). */
    dailyMinHours: 72,
    /** ...and this many (21 days) for a day-of-week x hour profile (168 buckets). */
    weeklyMinHours: 504,
    /** Below this many samples a bucket is "low confidence": callers fall back
     *  to the series-wide (flat) statistics rather than trust it. */
    minBucketSamples: 3,
    /** Per-refresh-job BigQuery cost cap (bytes billed); a job over it fails
     *  for that tenant only. */
    maxBytesBilledPerTenant: 5 * 1024 ** 3,
    /** Defensive cap on the read API's point lookup (a clustered, kilobyte-scale
     *  scan): an authenticated, externally callable endpoint must not be able to
     *  bill more than this per request. */
    maxBytesBilledPerRead: 256 * 1024 ** 2,
    /** Per-refresh-job deadline: BigQuery cancels (and rolls back) a job that
     *  runs longer, so one slow tenant cannot outlive the function. */
    refreshJobTimeoutMs: 100_000,
    /** The scheduled function's own limit... */
    sweepTimeoutSeconds: 540,
    /** ...and the point after which no NEW tenant is started. Invariant (tested):
     *  startBudgetMs + refreshJobTimeoutMs < sweepTimeoutSeconds * 1000, so the
     *  last job always finishes — and the summary is always logged — in time. */
    startBudgetMs: 420_000,
};
export const BASELINE_PROFILES = ['weekly', 'daily', 'flat'];
/** GET /resources/:id/baseline — strict; unknown or repeated params are a 400. */
export const baselineQuerySchema = z
    .object({
    sensorKey: sensorKeySchema,
    datapoint: datapointNameSchema,
    instanceId: instanceIdSchema.optional(),
    /** The moment whose bucket to describe; default now. */
    at: isoDateTimeSchema.optional(),
    /** Optional observed value to place against what is normal. */
    value: queryNumberSchema.optional(),
})
    .strict();
/** Distribution of the hourly values in one bucket. */
export const baselineStatsSchema = z
    .object({
    /** Hourly observations behind these numbers. */
    n: z.number().int().nonnegative(),
    mean: z.number().nullable(),
    stddev: z.number().nullable(),
    min: z.number().nullable(),
    p05: z.number().nullable(),
    p50: z.number().nullable(),
    p95: z.number().nullable(),
    p99: z.number().nullable(),
    max: z.number().nullable(),
})
    .strict();
export const BASELINE_POSITIONS = ['below', 'normal', 'above'];
export const baselineReadSchema = z
    .object({
    resourceId: idSchema,
    series: z
        .object({
        sensorKey: sensorKeySchema,
        instanceId: instanceIdSchema.nullable(),
        datapoint: datapointNameSchema,
    })
        .strict(),
    /** `unavailable`: no baseline yet (a new series needs >= 24h of data and
     *  the next daily refresh) — callers must not invent one. */
    status: z.enum(['ok', 'unavailable']),
    reason: z.string().nullable(),
    at: isoDateTimeSchema,
    timezone: timezoneSchema.nullable(),
    /** Seasonality actually used: `weekly` (day-of-week x hour), `daily`
     *  (hour-of-day), or `flat` (no seasonality yet). */
    profile: z.enum(BASELINE_PROFILES).nullable(),
    /** The bucket containing `at` — null on a flat profile. */
    bucket: z
        .object({
        profile: z.enum(['weekly', 'daily']),
        /** weekly: Mon=0..Sun=6 * 24 + hour; daily: hour 0-23. */
        key: z.number().int().nonnegative(),
        stats: baselineStatsSchema,
        /** n < BASELINE_MODEL.minBucketSamples: prefer `overall`. */
        lowConfidence: z.boolean(),
    })
        .strict()
        .nullable(),
    /** The series-wide distribution, whatever the time of day. */
    overall: baselineStatsSchema.nullable(),
    /** Robust sigma (1.4826 x MAD of the residuals around the seasonal
     *  median): spread that a few outage hours cannot inflate. */
    sigmaRobust: z.number().nullable(),
    window: z
        .object({ start: isoDateTimeSchema, end: isoDateTimeSchema, hours: z.number().int() })
        .strict()
        .nullable(),
    computedAt: isoDateTimeSchema.nullable(),
    modelVersion: z.number().int().nullable(),
    /** Present only when `value` was supplied. `basis` says which
     *  distribution it was compared against. */
    assessment: z
        .object({
        value: z.number(),
        position: z.enum(BASELINE_POSITIONS),
        /** (value - median) / sigmaRobust; null when sigma is 0/unknown. */
        robustZ: z.number().nullable(),
        basis: z.enum(['bucket', 'overall']),
    })
        .strict()
        .nullable(),
})
    .strict();
/* ------------------------------------------------------------------ */
/* Threshold proposals (#18 part 2)                                    */
/* ------------------------------------------------------------------ */
/**
 * Proposals turn a series' learned baseline into STATIC thresholds (alert
 * thresholds are static numbers), set above the seasonal busy-hour norm
 * (for `>`/`>=`) or below the seasonal quiet-hour norm (for `<`/`<=`) by a
 * multiple of the robust sigma. They are never applied automatically: a human
 * accepts (applied as an assignment override, audited) or dismisses.
 */
export const BASELINE_PROPOSAL = {
    /** Bumped whenever the proposal rule changes; part of the revision. */
    version: 1,
    /** Distance from the seasonal norm, in robust sigmas, per severity. */
    sigmaMultiplier: { warning: 3, error: 5, critical: 8 },
    /** Proposed numbers are rounded to this many significant digits. */
    significantDigits: 3,
};
export const thresholdProposalQuerySchema = z
    .object({ sensorKey: sensorKeySchema, datapoint: datapointNameSchema })
    .strict();
/** Accept / dismiss: the caller names the proposal revision it looked at, and
 *  the server re-derives the proposal — numbers are never taken from the client. */
export const thresholdProposalActionSchema = z
    .object({
    sensorKey: sensorKeySchema,
    datapoint: datapointNameSchema,
    revision: z.string().regex(/^[0-9a-f]{16}$/),
})
    .strict();
const proposalLevels = z
    .object({
    operator: z.enum(['>', '>=', '<', '<=', '==', '!=']),
    warning: z.number().nullable(),
    error: z.number().nullable(),
    critical: z.number().nullable(),
})
    .strict();
/** How many window hours breached each severity, at the current vs proposed numbers. */
const proposalEvidenceSchema = z
    .object({
    hours: z.number().int().nonnegative(),
    current: z.object({ warning: z.number().int().nullable(), error: z.number().int().nullable(), critical: z.number().int().nullable() }).strict(),
    proposed: z.object({ warning: z.number().int().nullable(), error: z.number().int().nullable(), critical: z.number().int().nullable() }).strict(),
})
    .strict();
export const THRESHOLD_PROPOSAL_STATUSES = ['proposed', 'unchanged', 'dismissed', 'unavailable'];
export const thresholdProposalReadSchema = z
    .object({
    resourceId: idSchema,
    sensorKey: sensorKeySchema,
    datapoint: datapointNameSchema,
    status: z.enum(THRESHOLD_PROPOSAL_STATUSES),
    reason: z.string().nullable(),
    /** Identifies this exact proposal (baseline run + current thresholds + rule
     *  version); accept/dismiss must quote it. */
    revision: z.string().nullable(),
    current: proposalLevels.nullable(),
    proposed: proposalLevels.nullable(),
    basis: z
        .object({
        profile: z.enum(['weekly', 'daily', 'flat']),
        /** The seasonal norm the levels are measured from (peak p95 / trough p05). */
        norm: z.number(),
        sigmaRobust: z.number(),
        windowStart: isoDateTimeSchema,
        windowEnd: isoDateTimeSchema,
        computedAt: isoDateTimeSchema,
    })
        .strict()
        .nullable(),
    evidence: proposalEvidenceSchema.nullable(),
})
    .strict();
/** A dismissed proposal revision. Keyed by (tenant, resource, sensor, datapoint);
 *  a NEW revision (new baseline run or changed thresholds) is shown again. */
export const thresholdProposalDismissalSchema = z
    .object({
    resourceId: idSchema,
    sensorKey: sensorKeySchema,
    datapoint: datapointNameSchema,
    revision: z.string().regex(/^[0-9a-f]{16}$/),
})
    .extend(baseEntityFields)
    .strict();
//# sourceMappingURL=baseline.schema.js.map